/**
 * The shared public-facing agenda/packet render (agenda-packet.md §6, §10A) — extracted out of
 * `pages/MeetingsPage.tsx` so the SAME render can be reused by:
 *  - the real public `/meetings/{id}` detail page (unchanged output — no drift), and
 *  - the staff builder's read-only "Preview — not yet published" panel (Feature 1), fed from the
 *    builder's CURRENT DRAFT data instead of a server response.
 *
 * Nested section/sub-item outline, narratives, per-item Outcome/Vote "Actions taken" recap,
 * attachment download links, and the KOMA-style notice block all live here — see the per-block doc
 * comments below for the accessibility reasoning (heading levels, list nesting, etc.) carried over
 * from the original MeetingsPage implementation.
 */
import { PrinterIcon, MailIcon, FileIcon, LinkIcon } from "./icons.js";
import type { PublicMeetingDetail, PublicAgendaItemView, AgendaState } from "../api/types.js";

export function formatDateLong(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

/** Render a stored 24-hour "HH:MM" start time as 12-hour "3:04 PM" (residents
 * expect am/pm, not 18:31). Empty stays empty; unparseable passes through. */
export function formatTime12(hhmm: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return hhmm;
  let h = Number(m[1]);
  const min = m[2];
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${min} ${ampm}`;
}

export function buildNoticeRequestMailto(
  townName: string,
  meeting: PublicMeetingDetail["meeting"] | undefined,
): string {
  const subject = `Meeting notice request — ${townName}`;
  const body = meeting
    ? `Please add me to the official notice list for ${meeting.body} meetings.\n\n(Regarding: ${meeting.body}${
        meeting.title ? ` — ${meeting.title}` : ""
      }, ${meeting.meetingDate})`
    : `Please add me to the official notice list for ${townName} meetings.`;
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/** Sanitize-trusting render — narrativeHtml/bodyHtml/summaryHtml are server-sanitized
 *  (SanitizeAnswerHTML) before they ever reach this client, same trust boundary as FaqPage. */
function TrustedHtml({ html, className = "" }: { html: string; className?: string }): JSX.Element {
  // eslint-disable-next-line react/no-danger
  return <div className={`td-faq-answer ${className}`} dangerouslySetInnerHTML={{ __html: html }} />;
}

/** Prominent, accessible "tentative agenda" banner (agenda-packet.md §10A tentative/final) — shown
 *  whenever the meeting's agendaState is "tentative" (including in the builder's Preview, which
 *  always reflects the state that WOULD be published — "tentative" being the default). Hidden for
 *  "final" or legacy/pre-state ("") meetings, so a meeting published before this feature shipped
 *  never shows a stale banner. */
function TentativeBanner({ agendaState }: { agendaState: AgendaState | undefined }): JSX.Element | null {
  if (agendaState !== "tentative") return null;
  return (
    <>
      <p className="td-alert td-alert--info td-mtg-tentative-banner" role="status">
        <strong>Tentative agenda</strong> — subject to change before the meeting.
      </p>
      {/* Item narratives + attachments are withheld server-side while tentative (titles/presenters/
          sub-item titles only — matches the staff "Titles Only" print option); this hint tells the
          reader why the outline looks sparse instead of leaving it unexplained. Reuses the same muted
          caption style as the notice block's own disclaimer line (td-notice-block__disclaimer) rather
          than introducing a new, separately-styled class for one line of text. */}
      <p className="td-notice-block__disclaimer td-mtg-tentative-hint">
        Full item details will be posted when the agenda is finalized.
      </p>
    </>
  );
}

/**
 * Renders ONE top-level agenda entry (agenda-packet.md nested §, Council Agenda 10A). A plain
 * (flat) item — no `children` — renders EXACTLY as before (a `<p>` title, no heading bump — WCAG
 * 1.3.1: don't introduce a heading level a flat meeting never needed). An item WITH `children`
 * becomes a SECTION: its own title is a genuine `<h2>` and its sub-items render as a nested `<ol>`
 * with each topic under an `<h3>` — a correct, non-skipping heading hierarchy under the page's
 * single "Agenda" `<h2>`... note the section title itself is styled as `.td-meeting-doc__item-title`
 * (visual weight matches a flat item) even though it's a semantic `<h2>` for screen-reader
 * navigation, so a section doesn't visually dwarf a flat sibling item.
 */
function AgendaItemBlock({ item, idPrefix }: { item: PublicAgendaItemView; idPrefix: string }): JSX.Element {
  const isSection = Boolean(item.children && item.children.length > 0);
  const titleText = `${item.order + 1}. ${item.title}${item.presenter ? ` (${item.presenter})` : ""}`;
  return (
    <li style={{ marginBottom: "var(--td-space-5)" }}>
      {isSection ? (
        <h2 className="td-meeting-doc__item-title" id={`${idPrefix}-section-${item.id}`}>
          {titleText}
        </h2>
      ) : (
        <p className="td-meeting-doc__item-title">{titleText}</p>
      )}
      {item.narrativeHtml ? <TrustedHtml html={item.narrativeHtml} /> : null}
      <AgendaAttachmentDownloads attachments={item.attachments} idPrefix={`${idPrefix}-${item.id}`} />
      {isSection ? (
        <ol className="td-meeting-doc__subitem-list" aria-label={`${item.title} — sub-items`}>
          {item.children!.map((sub) => (
            <li key={sub.id} style={{ marginTop: "var(--td-space-3)" }}>
              <h3>
                {sub.order + 1}. {sub.title}
                {sub.presenter ? ` (${sub.presenter})` : ""}
              </h3>
              {sub.narrativeHtml ? <TrustedHtml html={sub.narrativeHtml} /> : null}
              <AgendaAttachmentDownloads attachments={sub.attachments} idPrefix={`${idPrefix}-${sub.id}`} />
            </li>
          ))}
        </ol>
      ) : null}
    </li>
  );
}

/** Renders ONE attachment row — a FILE downloads as today; a LINK (`isLink: true`) gets an explicit
 *  external-link affordance (a ↗ indicator + "opens an external link" text + `target="_blank"
 *  rel="noopener noreferrer"`) so residents never mistake it for a hosted download. Never renders an
 *  `href` when `url` is missing/empty (e.g. the builder Preview's not-yet-published file rows) — the
 *  name renders as plain text instead. Both kinds keep the same ADA "may not be accessible" caveat. */
function AttachmentDownloadRow({ a }: { a: PublicAgendaItemView["attachments"][number] }): JSX.Element {
  const label = a.isLink ? (a.filename ?? "Linked document") : (a.filename ?? "Download");
  return (
    <div className="td-attach-download">
      <span className="td-attach-download__icon" aria-hidden="true">
        {a.isLink ? <LinkIcon width={18} height={18} /> : <FileIcon width={18} height={18} />}
      </span>
      <div>
        {a.url ? (
          <a className="td-attach-download__name" href={a.url} target="_blank" rel="noopener noreferrer">
            {label}
            {a.isLink ? (
              <>
                {" "}
                <span aria-hidden="true">↗</span>
                <span className="td-sr-only"> (opens an external link, not a hosted download)</span>
              </>
            ) : null}
          </a>
        ) : (
          <span className="td-attach-download__name">{label}</span>
        )}
        <p className="td-attach-download__desc">{a.description}</p>
        <p className="td-attach-download__caveat">
          This attachment {a.isLink ? "links to an external document" : "is a file download"} and may
          not meet accessibility standards; contact the clerk for an accessible copy.
        </p>
      </div>
    </div>
  );
}

/** Splits an item's attachments into INLINE (rendered directly in the agenda outline, as before) and
 *  SUPPORTING (`supporting: true` — grouped into a collapsed, accessible `<details>` expander below
 *  the inline content, Council Agenda 10A). Native `<details>`/`<summary>` is keyboard-operable and
 *  exposed to assistive tech out of the box — no extra ARIA needed. Renders no expander at all when
 *  an item has zero supporting docs. */
function AgendaAttachmentDownloads({
  attachments,
  idPrefix,
}: {
  attachments: PublicAgendaItemView["attachments"];
  idPrefix: string;
}): JSX.Element | null {
  if (attachments.length === 0) return null;
  const inline = attachments.filter((a) => !a.supporting);
  const supporting = attachments.filter((a) => a.supporting);
  return (
    <div>
      {inline.map((a) => (
        <AttachmentDownloadRow key={a.id} a={a} />
      ))}
      {supporting.length > 0 ? (
        <details className="td-supporting-docs" id={`${idPrefix}-supporting`}>
          <summary>Supporting documents ({supporting.length})</summary>
          <div className="td-supporting-docs__list">
            {supporting.map((a) => (
              <AttachmentDownloadRow key={a.id} a={a} />
            ))}
          </div>
        </details>
      ) : null}
    </div>
  );
}

/**
 * Renders the items whose Outcome is set — the public "Actions taken" recap (agenda-packet.md §9b
 * resolved decision #1), now GROUPED PER SECTION (Council Agenda 10A): each top-level item that has
 * sub-items with an Outcome shows its own sub-heading listing just those topics' outcomes; a
 * top-level item's OWN Outcome/Vote (a section-level action, not just its sub-items') also renders.
 * A flat top-level item with an Outcome (no children) renders exactly as before, unsectioned.
 */
function ActionsRecap({ items, idPrefix }: { items: PublicAgendaItemView[]; idPrefix: string }): JSX.Element | null {
  const anyOutcome = items.some((i) => i.outcome || (i.children ?? []).some((c) => c.outcome));
  if (!anyOutcome) return null;
  return (
    <section aria-labelledby={`${idPrefix}-actions-h`} style={{ marginBottom: "var(--td-space-6)" }}>
      <h2 id={`${idPrefix}-actions-h`} className="section-h">
        Actions taken
      </h2>
      {items.map((item) => {
        const children = item.children ?? [];
        const childOutcomes = children.filter((c) => c.outcome);
        if (!item.outcome && childOutcomes.length === 0) return null;
        return (
          <div key={item.id}>
            {item.outcome ? (
              <div className="td-actions-recap">
                <div className="td-actions-recap__row">
                  <span style={{ flex: 1 }}>
                    <strong>{item.title}</strong> — {item.outcome}
                  </span>
                  {item.vote ? <VotePill vote={item.vote} /> : null}
                </div>
              </div>
            ) : null}
            {childOutcomes.length > 0 ? (
              <div className="td-actions-recap">
                {childOutcomes.map((c) => (
                  <div className="td-actions-recap__row" key={c.id}>
                    <span style={{ flex: 1 }}>
                      <strong>{c.title}</strong> — {c.outcome}
                    </span>
                    {c.vote ? <VotePill vote={c.vote} /> : null}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </section>
  );
}

function VotePill({ vote }: { vote: PublicAgendaItemView["vote"] }): JSX.Element | null {
  if (!vote) return null;
  const passed = vote.ayes > vote.nays;
  return (
    <span
      className={`vote-pill ${passed ? "vote-pill--passed" : vote.nays > vote.ayes ? "vote-pill--failed" : "vote-pill--info"}`}
      aria-label={`Ayes ${vote.ayes}, Nays ${vote.nays}, Abstain ${vote.abstain}${vote.detail ? `. ${vote.detail}` : ""}`}
    >
      {vote.ayes}-{vote.nays}
      {vote.abstain ? `-${vote.abstain}` : ""}
    </span>
  );
}

/** Best-practice, non-legal-assertion notice block (agenda-packet.md §6 — KOMA is request-based
 *  and does NOT mandate a posted agenda; this is a transparency/ADA asset, not a compliance
 *  guarantee). "Request official notice" is a `mailto:` per Lister's §9b lock — the underlying
 *  KOMA notice-request process/contact is unconfirmed with counsel, so no in-product form yet. */
function NoticeBlock({
  meeting,
  mailto,
  idPrefix,
}: {
  meeting: PublicMeetingDetail["meeting"];
  mailto: string;
  idPrefix: string;
}): JSX.Element {
  return (
    <div className="td-notice-block" aria-labelledby={`${idPrefix}-notice-h`}>
      <h2 id={`${idPrefix}-notice-h`} className="section-h" style={{ marginTop: 0 }}>
        Meeting notice
      </h2>
      <dl>
        <div className="td-notice-block__row">
          <dt>Body</dt>
          <dd>{meeting.body}</dd>
        </div>
        <div className="td-notice-block__row">
          <dt>Date &amp; time</dt>
          <dd>
            {formatDateLong(meeting.meetingDate)}
            {meeting.startTime ? ` at ${formatTime12(meeting.startTime)}` : ""}
          </dd>
        </div>
        <div className="td-notice-block__row">
          <dt>Location</dt>
          <dd>{meeting.location}</dd>
        </div>
        <div className="td-notice-block__row">
          <dt>Request notice</dt>
          <dd>
            <a href={mailto}>
              <MailIcon width={14} height={14} aria-hidden="true" style={{ verticalAlign: "-2px" }} />{" "}
              Request to be added to the official notice list
            </a>
          </dd>
        </div>
        <div className="td-notice-block__row">
          <dt>Comment</dt>
          <dd>Public comment is welcome at the meeting, per the body's usual practice.</dd>
        </div>
      </dl>
      <p className="td-notice-block__disclaimer">
        Agenda items are subject to change. This page is provided for public transparency and
        accessibility; it is not a substitute for the town's official meeting notice process.
      </p>
    </div>
  );
}

/**
 * The full public agenda/packet render — notice block, print button, actions-taken recap, agenda
 * outline, and (when present) minutes. Shared 1:1 by the real public detail page and the staff
 * builder's Preview panel; `titleId` lets each caller own its own `<h1>` id for skip-link targets.
 */
export function PublicMeetingView({
  detail,
  townName,
  headingLevel = 1,
  showPrintButton = true,
  idPrefix = "mtg",
  landmarkSuffix,
}: {
  detail: PublicMeetingDetail;
  townName: string;
  /** The builder Preview renders inside a dialog that already has its own `<h1>` (the dialog
   *  label) — pass 2 there so the meeting title doesn't duplicate the top-level heading. */
  headingLevel?: 1 | 2;
  /** The Preview has no reason to trigger the browser print dialog on a draft. */
  showPrintButton?: boolean;
  /** Distinguishes this render's internal landmark/heading ids from another copy on the same page
   *  (the builder's Preview dialog renders alongside the builder's own "Agenda" section, which
   *  otherwise shares the default "mtg-agenda-h" id — WCAG landmark-unique). Defaults to "mtg", the
   *  original id prefix, so the real public page's output is byte-identical to before. */
  idPrefix?: string;
  /** Visually-hidden text appended to the "Agenda"/"Minutes" landmark headings' ACCESSIBLE name only
   *  (never the visible text) — the Preview dialog needs a distinct name from the builder's own
   *  "Agenda" section landmark on the same page (WCAG landmark-unique); the real public page leaves
   *  this unset so its rendered output is byte-identical to before. */
  landmarkSuffix?: string;
}): JSX.Element {
  const noticeRequestMailto = buildNoticeRequestMailto(townName, detail.meeting);
  const Heading = headingLevel === 1 ? "h1" : "h2";
  const srSuffix = landmarkSuffix ? <span className="td-sr-only"> {landmarkSuffix}</span> : null;

  return (
    <>
      <Heading className="td-intake__title">
        {detail.meeting.body}
        {detail.meeting.title ? ` — ${detail.meeting.title}` : ""}
      </Heading>

      <TentativeBanner agendaState={detail.meeting.agendaState} />

      <NoticeBlock meeting={detail.meeting} mailto={noticeRequestMailto} idPrefix={idPrefix} />

      {showPrintButton ? (
        <div className="td-mtg-print" style={{ marginBottom: "var(--td-space-4)" }}>
          <button type="button" className="td-btn td-btn--secondary" onClick={() => window.print()}>
            <PrinterIcon width={18} height={18} aria-hidden="true" />
            Print this page
          </button>
        </div>
      ) : null}

      <ActionsRecap items={detail.items} idPrefix={idPrefix} />

      {/* "On this page" jump-nav (bookmarks) — Lister/LV team, 2026-08-28: fast access to
          recap/minutes from the list without scrolling. Only lists sections that actually exist
          for THIS meeting (mirrors internal/meetingshtml's detail.html.tmpl exactly — same
          fixed #agenda/#recap/#minutes anchor ids, since the public API's in-page doc-links
          (handler.addEntityDocLinks) point at these literal fragments regardless of idPrefix). */}
      <nav className="td-jumpnav" aria-label="On this page">
        <a href="#agenda">Agenda</a>
        {detail.minutes?.summaryHtml ? (
          <>
            {" "}
            · <a href="#recap">Recap</a>
          </>
        ) : null}
        {detail.minutes?.bodyHtml ? (
          <>
            {" "}
            · <a href="#minutes">Minutes</a>
          </>
        ) : null}
      </nav>

      <div className="td-meeting-doc">
        <section id="agenda" className="td-meeting-doc__section" aria-labelledby={`${idPrefix}-agenda-h`}>
          <h2 id={`${idPrefix}-agenda-h`}>
            Agenda
            {srSuffix}
          </h2>
          {detail.items.length === 0 ? (
            <p>No agenda items posted.</p>
          ) : (
            <ol className="td-meeting-doc__agenda-list">
              {detail.items.map((item) => (
                <AgendaItemBlock key={item.id} item={item} idPrefix={idPrefix} />
              ))}
            </ol>
          )}
        </section>

        {/* Recap and Minutes are now TWO INDEPENDENT sections (previously nested as Summary/
            "Official minutes" sub-headings under one combined Minutes section) — split so each
            has its own stable, directly-linkable #recap/#minutes anchor, mirroring the Go
            prerender's detail.html.tmpl restructure exactly. */}
        {detail.minutes?.summaryHtml ? (
          <section id="recap" className="td-meeting-doc__section" aria-labelledby={`${idPrefix}-recap-h`}>
            <h2 id={`${idPrefix}-recap-h`}>
              Recap
              {srSuffix}
            </h2>
            <p className="td-recap-disclaimer" role="note">
              Unofficial recap — an AI-generated summary provided for convenience. It is not the
              official record; the official record is the approved meeting minutes.
            </p>
            <TrustedHtml html={detail.minutes.summaryHtml} />
          </section>
        ) : null}

        {detail.minutes?.bodyHtml ? (
          <section id="minutes" className="td-meeting-doc__section" aria-labelledby={`${idPrefix}-minutes-h`}>
            <h2 id={`${idPrefix}-minutes-h`}>
              Minutes
              {detail.minutes.adoptionState === "draft" ? (
                <span className="draft-label" style={{ marginLeft: "var(--td-space-2)" }}>
                  DRAFT — pending Council adoption
                </span>
              ) : null}
              {srSuffix}
            </h2>
            <TrustedHtml html={detail.minutes.bodyHtml} />
          </section>
        ) : null}
      </div>
    </>
  );
}
